'use strict'

import React, { ComponentProps, useState } from 'react'
import Icon from '@mdi/react'

import Box from '@components/kit/box'
import Text from '@components/kit/text'
import Pressable from '@components/kit/pressable'
import Loading from '@components/kit/loading'
import { CloseIcon } from '@components/kit/icons'
import { colors } from '@theme'
import {
  ATTACHMENT_ICON_SIZE,
  formatAttachmentSize,
  formatAttachmentType,
  getAttachmentIcon
} from './attachment-display'

type ThemeTextColor = ComponentProps<typeof Text>['textColor']

export interface FileAttachmentPreviewProps {
  file_name: string
  file_size: number
  mime_type: string
  variant?: 'message' | 'composer'
  textColor?: ThemeTextColor
  isSender?: boolean
  sendFailed?: boolean
  isLoading?: boolean
  onRemove?: () => void
  onPress?: () => void
}

function FileAttachmentPreview({
  file_name,
  file_size,
  mime_type,
  variant = 'message',
  textColor: textColorProp,
  isSender = false,
  sendFailed = false,
  isLoading = false,
  onRemove,
  onPress
}: FileAttachmentPreviewProps) {
  const [isHovered, setIsHovered] = useState(false)
  const isComposer = variant === 'composer'

  const textColor: ThemeTextColor =
    textColorProp ?? (isComposer ? 'shade07' : isSender ? 'white' : 'newPurple')

  const resolveColor = (token: ThemeTextColor) =>
    (colors as Record<string, string>)[String(token)] ?? String(token)

  const iconPath = getAttachmentIcon(
    mime_type,
    file_name,
    !isComposer && isHovered
  )
  const typeLabel = formatAttachmentType(mime_type, file_name)
  const sizeLabel = formatAttachmentSize(file_size)
  const metaLine = sizeLabel ? `${typeLabel} · ${sizeLabel}` : typeLabel
  const iconColor = resolveColor(textColor)
  const iconBackground = isComposer
    ? 'rgba(0, 0, 0, 0.06)'
    : isSender && !sendFailed
    ? 'rgba(255, 255, 255, 0.18)'
    : 'rgba(0, 0, 0, 0.06)'

  const content = (
    <>
      <Box
        width={ATTACHMENT_ICON_SIZE.container}
        height={ATTACHMENT_ICON_SIZE.container}
        alignItems='center'
        justifyContent='center'
        mr='12'
        style={{
          borderRadius: ATTACHMENT_ICON_SIZE.borderRadius,
          backgroundColor: iconBackground
        }}
      >
        {isLoading ? (
          <Loading size='small' fill={textColor} />
        ) : (
          <Icon
            path={iconPath}
            size={`${ATTACHMENT_ICON_SIZE.icon}px`}
            color={iconColor}
          />
        )}
      </Box>
      <Box flexDirection='column' flex='1 1 auto'>
        <Text font='text.14.semibold' textColor={textColor}>
          {file_name}
        </Text>
        <Text
          font='text.12.medium'
          textColor={textColor}
          style={{ opacity: 0.8 }}
        >
          {metaLine}
        </Text>
      </Box>
      {onRemove ? (
        <Pressable
          ml='08'
          onPress={onRemove}
          alignItems='center'
          justifyContent='center'
          style={{ width: 32, height: 32 }}
        >
          <CloseIcon width={20} height={20} fill='shade05' />
        </Pressable>
      ) : null}
    </>
  )

  if (isComposer) {
    return (
      <Box
        flex='1 1 auto'
        flexDirection='row'
        alignItems='center'
        py='12'
        px='16'
        bg='shade02'
        borderRadius='xlarge'
      >
        {content}
      </Box>
    )
  }

  return (
    <Pressable
      flexDirection='row'
      alignItems='center'
      onMouseEnter={() => !isLoading && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onPress={isLoading ? undefined : onPress}
    >
      {content}
    </Pressable>
  )
}

export default FileAttachmentPreview
